import { useEffect, useRef, useState } from 'react'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import './MapView.css'
import TileDownloader from './TileDownloader'
import TrackManager from './TrackManager'
import CacheInspector from './CacheInspector'
import PlaceSearch from './PlaceSearch'
import { CanvasContours } from './CanvasContours'
import { isSimulatorEnabled, getGPSSimulator } from '../utils/gpsSimulator'
import { saveTrack, saveMarker, getSavedMarkers, SavedTrack } from '../utils/storage'
import { BathymetryManager } from '../services/BathymetryManager'

interface MapViewProps {
  showBathymetry: boolean
  smoothContours?: boolean
}

function MapView({ showBathymetry, smoothContours = true }: MapViewProps) {
  const mapContainer = useRef<HTMLDivElement>(null)
  const map = useRef<maplibregl.Map | null>(null)
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null)

  const searchParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null
  const isE2ETestMode = !!searchParams?.has('e2e')

  // Default view: app previously started around Hauraki; for E2E we default to Bay of Plenty
  // to reduce accidental early Hauraki detail loads influencing test runs.
  const defaultCenter: [number, number] = isE2ETestMode ? [177.2, -37.9] : [175.0, -36.7]
  const defaultZoom: number = isE2ETestMode ? 11 : 12

  const [mapCenter, setMapCenter] = useState<[number, number]>(defaultCenter)
  const [mapZoom, setMapZoom] = useState<number>(defaultZoom)
  const [heading, setHeading] = useState<number>(0)
  const [speed, setSpeed] = useState<number>(0)
  const [accuracy, setAccuracy] = useState<number>(0)
  const [showDownloader, setShowDownloader] = useState(false)
  const [showTrackManager, setShowTrackManager] = useState(false)
  const [showCacheInspector, setShowCacheInspector] = useState(false)
  const [isTracking, setIsTracking] = useState(true)
  const [trackStartTime, setTrackStartTime] = useState<number>(Date.now())
  const [maxSpeed, setMaxSpeed] = useState<number>(0)
  const [totalSpeed, setTotalSpeed] = useState<number>(0)
  const [speedSamples, setSpeedSamples] = useState<number>(0)
  const [loadingBathymetry, setLoadingBathymetry] = useState(true)
  const [loadingPlaceNames, setLoadingPlaceNames] = useState(true)
  const [scaleDistance, setScaleDistance] = useState<number>(0)
  const [scaleUnit, setScaleUnit] = useState<'m' | 'km'>('m')
  const [scaleSegmentPercent, setScaleSegmentPercent] = useState<number>(16.67) // What % of screen each segment takes
  const boatMarker = useRef<maplibregl.Marker | null>(null)
  const gpsTrackRef = useRef<[number, number][]>([]) // Use ref instead of state to avoid re-renders
  const savedMarkersRef = useRef<maplibregl.Marker[]>([])
  const bathymetryManager = useRef<BathymetryManager | null>(null)
  const useSimulator = isSimulatorEnabled()
  const testHarnessRef = useRef<NavicaE2EControls | null>(null)

  // DEBUG: Log URL params to see what's actually happening
  // if (typeof window !== 'undefined') {
  //   console.log('Current URL Search Params:', window.location.search);
  // }

  const shouldShowRegionDebug = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('debugRegions')
  // Always use Canvas contours for better performance and dynamic loading
  const useCanvasContours = true
  // console.log('shouldShowRegionDebug:', shouldShowRegionDebug);

  const ensureRegionDebugOverlay = () => {
    // console.log('[debugRegions] ensureRegionDebugOverlay called', {
    //   hasMap: !!map.current,
    //   hasManager: !!bathymetryManager.current,
    //   shouldShow: shouldShowRegionDebug,
    //   styleLoaded: map.current?.isStyleLoaded()
    // })

    if (!map.current || !bathymetryManager.current || !shouldShowRegionDebug) {
      // console.log('[debugRegions] skipping: missing dependencies')
      return
    }
    // REMOVED isStyleLoaded check because font loading failures (404s) cause it to return false forever,
    // preventing the debug overlay from drawing.
    if (!map.current.getStyle()) {
      // console.log('[debugRegions] skipping: map style object missing')
      return
    }

    const sourceId = 'bathymetry-region-bounds'
    const fillLayerId = 'bathymetry-region-bounds-fill'
    const lineLayerId = 'bathymetry-region-bounds-outline'
    const labelsLayerId = 'bathymetry-region-bounds-labels'

    const regions = bathymetryManager.current.getRegions()
    const loadingStatus = bathymetryManager.current.getLoadingStatus()
    
    const featureCollection = {
      type: 'FeatureCollection' as const,
      features: regions.map((r) => {
        const [w, s, e, n] = r.bounds
        const isLoaded = loadingStatus.regions.includes(r.id)
        return {
          type: 'Feature' as const,
          properties: { 
            id: r.id, 
            name: r.name, 
            minZoom: r.minZoom,
            isLoaded,
            status: (r as any).status || (isLoaded ? 'Loaded' : 'Unloaded')
          },
          geometry: {
            type: 'Polygon' as const,
            coordinates: [
              [
                [w, s],
                [e, s],
                [e, n],
                [w, n],
                [w, s],
              ],
            ],
          },
        }
      }),
    }

    console.log('[debugRegions] 🛠️ GENERATED GEOJSON:', JSON.stringify(featureCollection));

    try {
      if (!map.current.getSource(sourceId)) {
        map.current.addSource(sourceId, {
          type: 'geojson',
          data: featureCollection,
        })
      } else {
        const src = map.current.getSource(sourceId) as any
        if (src?.setData) {
          src.setData(featureCollection)
        }
      }

      // Fill first so it's visible even when you're *inside* a region
      if (!map.current.getLayer(fillLayerId)) {
        map.current.addLayer({
          id: fillLayerId,
          type: 'fill',
          source: sourceId,
          paint: {
            'fill-color': [
              'case',
              ['get', 'isLoaded'], '#00FF00', // GREEN if loaded
              '#00FFFF'                       // CYAN if not loaded
            ],
            'fill-opacity': 0.05,  // Very low opacity so we can see lines through it
          },
        })
      } else {
        map.current.moveLayer(fillLayerId)
      }

      if (!map.current.getLayer(lineLayerId)) {
        map.current.addLayer({
          id: lineLayerId,
          type: 'line',
          source: sourceId,
          paint: {
            'line-color': [
              'case',
              ['get', 'isLoaded'], '#00FF00', // GREEN if loaded
              '#00FFFF'                       // CYAN if not loaded
            ],
            'line-width': 5,
            'line-opacity': 1.0,
          },
        })
      } else {
        map.current.moveLayer(lineLayerId)
      }

      // Labels
      if (!map.current.getLayer(labelsLayerId)) {
        map.current.addLayer({
          id: labelsLayerId,
          type: 'symbol',
          source: sourceId,
          layout: {
            'text-field': ['concat', ['get', 'name'], '\n', ['get', 'status']],
            'text-size': 16,
            // REMOVED FONT SPEC to use default system font and avoid 404 errors crashing the layer
            'text-allow-overlap': true,
            'text-ignore-placement': true
          },
          paint: {
            'text-color': [
              'case',
              ['get', 'isLoaded'], '#00FF00', // GREEN if loaded
              '#00FFFF'                       // CYAN if not loaded
            ],
            'text-halo-color': '#000000',
            'text-halo-width': 3,
            'text-opacity': 1.0,
          },
        })
      } else {
        map.current.moveLayer(labelsLayerId)
      }
    } catch (err: any) {
      alert(`❌ ERROR DRAWING REGIONS:\n${err.message}`);
      console.error(err);
    }

    // console.log('[debugRegions] ensured overlay', {
    //   regions: regions.map(r => r.id),
    //   bounds: regions.map(r => r.bounds),
    //   hasSource: !!map.current.getSource(sourceId),
    //   hasFill: !!map.current.getLayer(fillLayerId),
    //   hasLine: !!map.current.getLayer(lineLayerId),
    // })
  }

  const publishTestHarness = () => {
    if (!isE2ETestMode || typeof window === 'undefined' || !map.current) {
      return
    }

    const harness: NavicaE2EControls = {
      isReady: () => true,
      setView: (center, zoom) => {
        map.current!.jumpTo({ center, zoom, bearing: 0, pitch: 0 })
      },
      getViewState: () => {
        if (!map.current) {
          return { center: [0, 0], zoom: 0, bounds: [0, 0, 0, 0] }
        }

        const center = map.current.getCenter()
        const bounds = map.current.getBounds()

        return {
          center: [center.lng, center.lat],
          zoom: map.current.getZoom(),
          bounds: [bounds.getWest(), bounds.getSouth(), bounds.getEast(), bounds.getNorth()]
        }
      },
      waitForIdle: () => new Promise<void>((resolve) => {
        const instance = map.current
        if (!instance) {
          resolve()
          return
        }

        const settleIfReady = () => {
          if (!instance) {
            cleanup()
            resolve()
            return
          }
          if (!instance.isMoving() && instance.areTilesLoaded()) {
            cleanup()
            resolve()
          }
        }

        const cleanup = () => {
          instance?.off('render', settleIfReady)
          instance?.off('idle', settleIfReady)
        }

        // Fast path if map is already idle and tiles loaded
        if (!instance.isMoving() && instance.areTilesLoaded()) {
          resolve()
          return
        }

        instance.on('render', settleIfReady)
        instance.on('idle', settleIfReady)

        // Safety timeout so tests never hang forever
        setTimeout(() => {
          cleanup()
          resolve()
        }, 5000)
      }),
      isRegionLoaded: (regionId) => {
        const status = bathymetryManager.current?.getLoadingStatus()
        return status?.regions.includes(regionId) ?? false
      },
      getRegionFeatureCount: (regionId) => {
        return bathymetryManager.current?.getRegionFeatureCount(regionId) ?? 0
      },
      getBaseFeatureCount: () => {
        return bathymetryManager.current?.getBaseFeatureCount() ?? 0
      },
      getLoadingStatus: () => bathymetryManager.current?.getLoadingStatus() ?? { base: false, regions: [] },
      focusRegionSample: (regionId, zoom = 11) => {
        const sample = bathymetryManager.current?.getSampleCoordinate(regionId)
        if (!sample) return false
        map.current!.jumpTo({ center: sample, zoom, bearing: 0, pitch: 0 })
        return true
      },
      countRenderedFeatures: (regionId) => {
        if (!map.current) return 0
        const layerId = `bathymetry-detail-lines-${regionId}`
        if (!map.current.getLayer(layerId)) {
          return 0
        }

        const visibility = map.current.getLayoutProperty(layerId, 'visibility') ?? 'visible'
        const opacity = map.current.getPaintProperty(layerId, 'line-opacity')
        const canvas = map.current.getCanvas()
        const container = map.current.getContainer()
        const width = container.clientWidth || canvas.width
        const height = container.clientHeight || canvas.height
        const queryBox: [maplibregl.PointLike, maplibregl.PointLike] = [[0, 0], [width, height]]
        const features = map.current.queryRenderedFeatures(queryBox, { layers: [layerId] })
        console.log('[E2E] countRenderedFeatures', regionId, {
          tilesLoaded: map.current.areTilesLoaded(),
          featureCount: features.length,
          viewport: { width, height },
          visibility,
          opacity
        })
        return features.length
      },
      measureBathymetryPixels: () => {
        if (!map.current) {
          return { bathyPixels: 0, sampledPixels: 0, ratio: 0 }
        }

        try {
          const canvas = map.current.getCanvas()
          const width = canvas.width
          const height = canvas.height
          if (!width || !height) {
            return { bathyPixels: 0, sampledPixels: 0, ratio: 0 }
          }

          const scratch = document.createElement('canvas')
          scratch.width = width
          scratch.height = height
          const ctx = scratch.getContext('2d')
          if (!ctx) {
            return { bathyPixels: 0, sampledPixels: 0, ratio: 0 }
          }

          ctx.drawImage(canvas, 0, 0)
          const image = ctx.getImageData(0, 0, width, height).data
          const minX = Math.floor(width * 0.2)
          const maxX = Math.floor(width * 0.8)
          const minY = Math.floor(height * 0.2)
          const maxY = Math.floor(height * 0.8)
          // Contour lines are thin; sample fairly densely so we don't miss them.
          const step = Math.max(1, Math.floor(window.devicePixelRatio)) * 2
          let bathyPixels = 0
          let sampledPixels = 0

          for (let y = minY; y < maxY; y += step) {
            for (let x = minX; x < maxX; x += step) {
              const idx = (y * width + x) * 4
              const r = image[idx]
              const g = image[idx + 1]
              const b = image[idx + 2]
              const a = image[idx + 3]
              sampledPixels++
              if (a < 40) {
                continue
              }
              // Detect our blue-ish contour palette on a dark background.
              const isBathymetryBlue = b > 110 && b > r && b > g
              if (isBathymetryBlue) {
                bathyPixels++
              }
            }
          }

          const ratio = sampledPixels > 0 ? bathyPixels / sampledPixels : 0
          const result = { bathyPixels, sampledPixels, ratio }
          console.log('[E2E] measureBathymetryPixels', result)
          return result
        } catch (error) {
          console.error('[E2E] measureBathymetryPixels error', error)
          return { bathyPixels: 0, sampledPixels: 0, ratio: 0 }
        }
      }
    }

    testHarnessRef.current = harness
    window.__NAVICA_E2E__ = harness
  }

  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined' && window.__NAVICA_E2E__) {
        delete window.__NAVICA_E2E__
      }
      testHarnessRef.current = null
    }
  }, [isE2ETestMode])

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || map.current) return

    // Default center: Central Hauraki Gulf (between Tiritiri and Coromandel)
    const defaultCenter: [number, number] = [175.0, -36.7]

    const productionStyle = {
      version: 8 as const,
      glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf',
      sources: {
        'osm': {
          type: 'raster' as const,
          tiles: [
            'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
            'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
            'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png'
          ],
          tileSize: 256,
          attribution: '© OpenStreetMap contributors',
          maxzoom: 19,
        },
      },
      layers: [
        {
          id: 'osm',
          type: 'raster' as const,
          source: 'osm',
          minzoom: 0,
          maxzoom: 19,
        },
      ],
    }

    const e2eStyle = {
      version: 8 as const,
      glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf',
      sources: {},
      layers: [
        {
          id: 'navica-e2e-background',
          type: 'background' as const,
          paint: {
            'background-color': '#101118'
          }
        }
      ]
    }

    map.current = new maplibregl.Map({
      container: mapContainer.current,
      style: isE2ETestMode ? e2eStyle : productionStyle,
      center: defaultCenter,
      zoom: 11,
      maxZoom: 19,
    })

    // Add navigation controls to bottom-right (clear of control panel)
    map.current.addControl(new maplibregl.NavigationControl(), 'bottom-right')

    // Create custom boat marker SVG
    const boatEl = document.createElement('div')
    boatEl.className = 'boat-marker'
    boatEl.innerHTML = `
      <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
        <g transform="translate(20, 20)">
          <!-- Boat shape pointing up (north) -->
          <path d="M 0,-15 L 5,10 L 0,15 L -5,10 Z" fill="#FF6B35" stroke="#000" stroke-width="1.5"/>
          <circle cx="0" cy="0" r="3" fill="#FFF" stroke="#000" stroke-width="1"/>
        </g>
      </svg>
    `

    // Function to add sources and layers (called when map is ready)
    const initializeMapLayers = () => {
      if (!map.current) return

      // Add GPS track source
      if (!map.current.getSource('gps-track')) {
        map.current.addSource('gps-track', {
          type: 'geojson',
          data: {
            type: 'Feature',
            properties: {},
            geometry: {
              type: 'LineString',
              coordinates: [],
            },
          },
        })

        map.current.addLayer({
          id: 'gps-track-line',
          type: 'line',
          source: 'gps-track',
          paint: {
            'line-color': '#FF6B35',
            'line-width': 3,
            'line-opacity': 0.8,
          },
        })
      }

      // Initialize the new BathymetryManager for smart zoom-based loading
      if (!useCanvasContours) {
        // Initialize the BathymetryManager (MapLibre layers + static GeoJSON files)
        console.log('🌊 Initializing BathymetryManager...')
        bathymetryManager.current = new BathymetryManager(map.current, {
          onStatusChange: () => {
            if (shouldShowRegionDebug) {
              ensureRegionDebugOverlay();
            }
          }
        })

        // Optional: draw region bounds as red rectangles for debugging.
        ensureRegionDebugOverlay()
      } else {
        console.log('🌊 Canvas contours mode: skipping BathymetryManager (no /data/bathymetry-*-detail.geojson fetches)')
        // Ensure it isn't accidentally left around from a previous initialization.
        bathymetryManager.current?.destroy()
        bathymetryManager.current = null
      }

      publishTestHarness()

      // Load bathymetry using Canvas renderer (not MapLibre layers)
      console.log('🌊 Using Canvas contours for bathymetry rendering')
      setLoadingBathymetry(false)

      // Add place names (islands, bays, marinas, etc.)
      if (!map.current.getSource('place-names')) {
        fetch('/data/place-names.geojson')
          .then(response => {
            if (!response.ok) throw new Error('Failed to load place names')
            return response.json()
          })
          .then(data => {
            if (!map.current) return

            map.current.addSource('place-names', {
              type: 'geojson',
              data: data,
            })

            // Add place name labels with styling based on feature type
            map.current.addLayer({
              id: 'place-labels',
              type: 'symbol',
              source: 'place-names',
              layout: {
                'text-field': ['get', 'name'],
                'text-size': [
                  'match',
                  ['get', 'type'],
                  'city', 16,
                  'town', 14,
                  'island', 13,
                  'village', 11,
                  'bay', 11,
                  'marina', 11,
                  'strait', 11,
                  'beach', 10,
                  'reef', 9,
                  'rock', 9,
                  'seamark', 8,
                  10  // default
                ],
                'text-font': ['Open Sans Bold', 'Arial Unicode MS Bold'],
                'text-anchor': 'center',
                'text-offset': [0, 0.5],
                'text-allow-overlap': false,
            'text-ignore-placement': false,
          },
          paint: {
            'text-color': [
              'match',
              ['get', 'type'],
              'island', '#2E5016',      // Dark green for islands
              'city', '#000000',        // Black for cities
              'town', '#333333',        // Dark gray for towns
              'village', '#555555',     // Gray for villages
              'bay', '#0066CC',         // Blue for bays
              'marina', '#CC3300',      // Red for marinas
              'strait', '#0066CC',      // Blue for straits
              'beach', '#D4A574',       // Sandy color for beaches
              'reef', '#CC0000',        // Red for reefs (danger)
              'rock', '#666666',        // Gray for rocks
              'seamark', '#FF6600',     // Orange for navigation marks
              '#000000'  // default black
            ],
            'text-halo-color': '#FFFFFF',
            'text-halo-width': 2,
            'text-halo-blur': 1,
            'text-opacity': 1,
          },
          minzoom: 9,  // Only show at reasonable zoom levels
            })

            setLoadingPlaceNames(false)
            console.log('✅ Place names loaded successfully')
          })
          .catch(error => {
            console.error('Failed to load place names:', error)
            setLoadingPlaceNames(false)
          })
      }
    }

    // Function to calculate and update custom scale ruler
    const updateScale = () => {
      if (!map.current) return

      try {
        const center = map.current.getCenter()
        const zoom = map.current.getZoom()
        const metersPerPixel = 156543.03392 * Math.cos(center.lat * Math.PI / 180) / Math.pow(2, zoom)
        const viewportWidth = map.current.getContainer().clientWidth
        const totalMeters = metersPerPixel * viewportWidth

        // Find the largest "nice" distance that is ~1/6th of the viewport
        const distances = [10, 20, 50, 100, 200, 500, 1000, 2000, 5000, 10000, 20000, 50000, 100000, 200000, 500000, 1000000]
        const targetSegmentMeters = totalMeters / 6
        let segmentDistance = distances[0]
        for (let i = distances.length - 1; i >= 0; i--) {
          if (distances[i] <= targetSegmentMeters) {
            segmentDistance = distances[i]
            break
          }
        }

        const segmentPercent = (segmentDistance / totalMeters) * 100
        if (segmentDistance >= 1000) {
          setScaleDistance(segmentDistance / 1000)
          setScaleUnit('km')
        } else {
          setScaleDistance(segmentDistance)
          setScaleUnit('m')
        }
        setScaleSegmentPercent(segmentPercent)
      } catch (error) {
        console.error('Scale calculation error:', error)
      }
    }

    // Always wait for the 'load' event to ensure style is ready
    map.current.once('load', () => {
      initializeMapLayers()

      // If debugRegions is enabled, keep the overlay present even if style data updates.
      if (shouldShowRegionDebug && map.current) {
        map.current.on('styledata', () => {
          ensureRegionDebugOverlay()
        })
      }

      // Track map movements
      map.current!.on('moveend', () => {
        if (map.current) {
          const center = map.current.getCenter()
          const zoom = map.current.getZoom()
          setMapCenter([center.lng, center.lat])
          setMapZoom(zoom)
        }
        updateScale()
      })

      map.current!.on('zoomend', () => {
        updateScale()
        if (bathymetryManager.current && map.current) {
          bathymetryManager.current.checkDetailLoading(map.current.getZoom())
        }
      })
      
      // Initial scale calculation - use 'idle' event (fires when map is fully rendered)
      map.current!.once('idle', () => {
        console.log('[Scale] 🎯 Map idle (fully rendered) - running initial scale update')
        updateScale()
      })
    })

    // Set up geolocation tracking (use simulator if ?simulate=true)
    const geoProvider = useSimulator ? getGPSSimulator() : navigator.geolocation
    
    if (isE2ETestMode) {
      console.log('🧪 E2E mode enabled - skipping geolocation tracking')
    } else if (geoProvider) {
      let isFirstLocation = true
      
      console.log(useSimulator ? '🎮 Using GPS Simulator' : '📍 Using Real GPS')
      
      const watchId = geoProvider.watchPosition(
        (position: any) => {
          const lon = position.coords.longitude
          const lat = position.coords.latitude
          const currentHeading = position.coords.heading || 0
          const currentSpeed = position.coords.speed || 0
          const gpsAccuracy = position.coords.accuracy

          console.log('GPS Update:', {
            latitude: lat,
            longitude: lon,
            accuracy: gpsAccuracy,
            heading: currentHeading,
            speed: currentSpeed,
            timestamp: new Date(position.timestamp).toISOString(),
            simulator: useSimulator,
          })

          setUserLocation([lon, lat])
          setHeading(currentHeading)
          setSpeed(currentSpeed)
          setAccuracy(gpsAccuracy)

          // Track speed statistics for saving
          if (currentSpeed > maxSpeed) {
            setMaxSpeed(currentSpeed)
          }
          setTotalSpeed(prev => prev + currentSpeed)
          setSpeedSamples(prev => prev + 1)

          // Center map on first GPS lock
          if (isFirstLocation && map.current) {
            map.current.flyTo({
              center: [lon, lat],
              zoom: 16,
              essential: true,
            })
            isFirstLocation = false
          }

          // Create or update boat marker
          if (!boatMarker.current && map.current) {
            boatMarker.current = new maplibregl.Marker({
              element: boatEl,
              anchor: 'center',
              rotationAlignment: 'map',
            })
              .setLngLat([lon, lat])
              .addTo(map.current!)
          } else if (boatMarker.current) {
            boatMarker.current.setLngLat([lon, lat])
          }

          // Rotate boat based on heading
          if (currentHeading !== null && currentHeading >= 0) {
            boatEl.style.transform = `rotate(${currentHeading}deg)`
          }

          // Add to GPS track if tracking is enabled
          if (isTracking) {
            const newTrack = [...gpsTrackRef.current, [lon, lat] as [number, number]]
            gpsTrackRef.current = newTrack
            
            // Update track line on map
            if (map.current?.getSource('gps-track')) {
              (map.current.getSource('gps-track') as maplibregl.GeoJSONSource).setData({
                type: 'Feature',
                properties: {},
                geometry: {
                  type: 'LineString',
                  coordinates: newTrack,
                },
              })
            }
          }
        },
        (error) => {
          console.error('GPS error:', error)
        },
        {
          enableHighAccuracy: true,
          timeout: 30000, // 30 seconds - gives GPS time to get initial fix
          maximumAge: 10000, // Accept positions up to 10 seconds old
        }
      )

      // Cleanup
      return () => {
        if (useSimulator) {
          getGPSSimulator().clearWatch(watchId)
        } else {
          navigator.geolocation.clearWatch(watchId)
        }
        
        // Clean up bathymetry manager
        if (bathymetryManager.current) {
          bathymetryManager.current.destroy()
          bathymetryManager.current = null
        }
        
        map.current?.remove()
        map.current = null
      }
    }

    // Add right-click context menu for markers
    map.current.on('contextmenu', (e) => {
      const markerName = prompt('Enter marker name:')
      if (markerName && map.current) {
        const coords: [number, number] = [e.lngLat.lng, e.lngLat.lat]
        const notes = prompt('Notes (optional):') || undefined
        saveMarker(markerName, coords, undefined, notes)
        
        // Add marker to map
        addMarkerToMap(coords, markerName)
        alert(`Marker "${markerName}" saved!`)
      }
    })

    // Load saved markers
    const loadSavedMarkers = () => {
      const savedMarkers = getSavedMarkers()
      savedMarkers.forEach((marker) => {
        addMarkerToMap(marker.coordinates, marker.name)
      })
    }
    loadSavedMarkers()

    // Add scale control (built-in MapLibre one at bottom-left)
    map.current.addControl(new maplibregl.ScaleControl(), 'bottom-left')
  }, [])

  // Helper function to add marker to map
  const addMarkerToMap = (coords: [number, number], name: string) => {
    if (!map.current) return
    
    const el = document.createElement('div')
    el.className = 'custom-marker'
    el.innerHTML = '📍'
    el.style.fontSize = '24px'
    el.title = name
    
    const marker = new maplibregl.Marker({ element: el })
      .setLngLat(coords)
      .addTo(map.current)
    
    savedMarkersRef.current.push(marker)
  }

  // Save current track
  const handleSaveTrack = () => {
    if (gpsTrackRef.current.length < 2) {
      alert('Track too short to save (need at least 2 points)')
      return
    }
    
    const trackName = prompt('Enter track name:')
    if (!trackName) return
    
    const duration = (Date.now() - trackStartTime) / 1000 // seconds
    const avgSpeed = speedSamples > 0 ? totalSpeed / speedSamples : 0
    
    saveTrack(trackName, gpsTrackRef.current, duration, maxSpeed, avgSpeed)
    alert(`Track "${trackName}" saved! (${gpsTrackRef.current.length} points)`)
  }

  // Clear current track
  const handleClearTrack = () => {
    if (confirm('Clear current track?')) {
      gpsTrackRef.current = []
      setTrackStartTime(Date.now())
      setMaxSpeed(0)
      setTotalSpeed(0)
      setSpeedSamples(0)
      
      if (map.current?.getSource('gps-track')) {
        (map.current.getSource('gps-track') as maplibregl.GeoJSONSource).setData({
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: [],
          },
        })
      }
    }
  }

  // Load a saved track
  const handleLoadTrack = (track: SavedTrack) => {
    if (map.current?.getSource('gps-track')) {
      (map.current.getSource('gps-track') as maplibregl.GeoJSONSource).setData({
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: track.coordinates,
        },
      })
      
      // Fly to track bounds
      const bounds = track.coordinates.reduce(
        (bounds, coord) => bounds.extend(coord),
        new maplibregl.LngLatBounds(track.coordinates[0], track.coordinates[0])
      )
      map.current.fitBounds(bounds, { padding: 50 })
    }
  }

  // Reload markers from storage
  const handleLoadMarkers = () => {
    // Clear existing markers
    savedMarkersRef.current.forEach(m => m.remove())
    savedMarkersRef.current = []
    
    // Load fresh markers
    const savedMarkers = getSavedMarkers()
    savedMarkers.forEach((marker) => {
      addMarkerToMap(marker.coordinates, marker.name)
    })
  }

  // Fly to marker
  const handleFlyToMarker = (coords: [number, number]) => {
    if (map.current) {
      map.current.flyTo({
        center: coords,
        zoom: 16,
        essential: true,
      })
    }
  }

  // Handle place search selection
  const handlePlaceSelect = (coordinates: [number, number], _name: string) => {
    if (map.current) {
      map.current.flyTo({
        center: coordinates,
        zoom: 14,
        essential: true,
      })
      
      // Flash the selected place by temporarily adding a marker
      const marker = new maplibregl.Marker({ color: '#FF6600' })
        .setLngLat(coordinates)
        .addTo(map.current)
      
      setTimeout(() => marker.remove(), 3000)  // Remove after 3 seconds
    }
  }

  // Update layer visibility when showBathymetry changes
  useEffect(() => {
    try {
      console.log('🌊 Visibility useEffect triggered, showBathymetry:', showBathymetry);
      
      if (!map.current) {
        console.log('🌊 Map not initialized, skipping visibility update');
        return;
      }

      // Check if map is ready and style is loaded
      if (!map.current.isStyleLoaded()) {
        console.log('🌊 Map style not yet loaded, skipping visibility update');
        return;
      }

      // List all current layers for debugging (only if style is loaded)
      const style = map.current.getStyle();
      if (style && style.layers) {
        const bathymetryLayers = style.layers.filter(layer => layer.id.includes('bathymetry'));
        console.log('🌊 Current bathymetry layers:', bathymetryLayers.map(l => l.id));
      } else {
        console.log('🌊 Map style not available, skipping layer enumeration');
        return;
      }

    // Update base layer visibility
    if (map.current.getLayer('bathymetry-base-lines')) {
      console.log('🌊 Setting bathymetry-base-lines opacity to:', showBathymetry ? 0.7 : 0);
      map.current.setPaintProperty(
        'bathymetry-base-lines',
        'line-opacity',
        showBathymetry ? 0.7 : 0
      )
    } else {
      console.log('🌊 ❌ bathymetry-base-lines layer not found');
    }

    if (map.current.getLayer('bathymetry-base-labels')) {
      console.log('🌊 Setting bathymetry-base-labels opacity to:', showBathymetry ? 1 : 0);
      map.current.setPaintProperty(
        'bathymetry-base-labels',
        'text-opacity',
        showBathymetry ? 1 : 0
      )
    } else {
      console.log('🌊 ❌ bathymetry-base-labels layer not found');
    }

    // Update detailed layers visibility (if any are loaded)
    if (bathymetryManager.current && map.current) {
      const status = bathymetryManager.current.getLoadingStatus()
      console.log('🌊 BathymetryManager status:', status);
      status.regions.forEach(regionId => {
        const detailLayerId = `bathymetry-detail-lines-${regionId}`
        const detailLabelsId = `bathymetry-detail-labels-${regionId}`
        
        if (map.current!.getLayer(detailLayerId)) {
          console.log(`🌊 Setting ${detailLayerId} opacity to:`, showBathymetry ? 0.8 : 0);
          map.current!.setPaintProperty(detailLayerId, 'line-opacity', showBathymetry ? 0.8 : 0)
        } else {
          console.log(`🌊 ❌ ${detailLayerId} layer not found`);
        }
        if (map.current!.getLayer(detailLabelsId)) {
          console.log(`🌊 Setting ${detailLabelsId} opacity to:`, showBathymetry ? 1 : 0);
          map.current!.setPaintProperty(detailLabelsId, 'text-opacity', showBathymetry ? 1 : 0)
        } else {
          console.log(`🌊 ❌ ${detailLabelsId} layer not found`);
        }
      })
    } else {
      console.log('🌊 ❌ BathymetryManager not initialized or map not available');
    }
    } catch (error) {
      console.error('🌊 ❌ Error in visibility useEffect:', error);
    }
  }, [showBathymetry])

  return (
    <div className="map-view">
      <div ref={mapContainer} className="map-container" data-testid="map-container" />

      {/* Canvas-based contours - bypasses MapLibre completely */}
      {useCanvasContours && (
        <CanvasContours map={map.current} visible={showBathymetry} smoothLines={smoothContours} />
      )}

      {shouldShowRegionDebug && (
        <div
          onClick={() => {
            const count = bathymetryManager.current?.getRegions().length ?? 0;
            alert(`🛠️ Debugging: Found ${count} regions.\n\nCheck the Console (F12) for the "[debugRegions] 🛠️ GENERATED GEOJSON" log.\n\nIf count is 0, the manager is empty.`);
            ensureRegionDebugOverlay();
          }}
          style={{
            position: 'absolute',
            top: 64,
            left: 12,
            zIndex: 9999,
            padding: '6px 10px',
            background: 'rgba(0,0,0,0.65)',
            color: '#ff4040',
            border: '2px solid #ff0000',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 700,
            pointerEvents: 'auto',
            cursor: 'pointer',
          }}
        >
          DEBUG REGIONS ON
        </div>
      )}
      
      {/* Place Search */}
      <div className="search-overlay">
        <PlaceSearch onSelectPlace={handlePlaceSelect} />
      </div>
      
      {/* Track Manager Panel */}
      {showTrackManager && (
        <TrackManager 
          onLoadTrack={handleLoadTrack}
          onLoadMarkers={handleLoadMarkers}
          onFlyToMarker={handleFlyToMarker}
        />
      )}
      
      {userLocation && (
        <div className="location-info">
          <div>📍 Lat: {userLocation[1].toFixed(6)}, Lon: {userLocation[0].toFixed(6)}</div>
          <div>🎯 Accuracy: ±{accuracy.toFixed(0)}m</div>
          <div>🧭 {heading >= 0 ? `${Math.round(heading)}°` : 'N/A'}</div>
          <div>⚡ {(speed * 1.94384).toFixed(1)} knots</div>
          {useSimulator && <div className="simulator-badge">🎮 SIMULATOR</div>}
        </div>
      )}
      
      {/* Main Control Panel - Right Side */}
      <div className="control-panel">
        {/* Navigation */}
        {userLocation && (
          <button
            className="control-btn"
            onClick={() => {
              if (map.current && userLocation) {
                map.current.flyTo({
                  center: userLocation,
                  zoom: 16,
                  essential: true,
                })
              }
            }}
            title="Center on my location"
          >
            🎯
          </button>
        )}

        {/* Track Controls */}
        <button
          className="control-btn"
          onClick={() => setIsTracking(!isTracking)}
          title={isTracking ? 'Pause tracking' : 'Resume tracking'}
        >
          {isTracking ? '⏸️' : '▶️'}
        </button>
        <button
          className="control-btn"
          onClick={handleSaveTrack}
          title="Save current track"
        >
          💾
        </button>
        <button
          className="control-btn"
          onClick={handleClearTrack}
          title="Clear current track"
        >
          🗑️
        </button>
        <button
          className="control-btn"
          onClick={() => setShowTrackManager(!showTrackManager)}
          title="Manage saved tracks and markers"
        >
          {showTrackManager ? '✖️' : '📂'}
        </button>

        {/* Download Maps */}
        <button
          className="control-btn"
          onClick={() => setShowDownloader(!showDownloader)}
          title="Download offline maps"
        >
          {showDownloader ? '✖️' : '📥'}
        </button>

        {/* Cache Inspector */}
        <button
          className="control-btn"
          onClick={() => setShowCacheInspector(!showCacheInspector)}
          title="View cached tiles"
        >
          {showCacheInspector ? '✖️' : '💾'}
        </button>
      </div>
      
      <div className="map-legend">
        <div className="legend-title">Depth (m)</div>
        <div className="legend-item">
          <span className="legend-color legend-color-10m"></span>
          <span>10-20m</span>
        </div>
        <div className="legend-item">
          <span className="legend-color legend-color-20m"></span>
          <span>20-30m</span>
        </div>
        <div className="legend-item">
          <span className="legend-color legend-color-30m"></span>
          <span>30-40m</span>
        </div>
        <div className="legend-item">
          <span className="legend-color legend-color-40m"></span>
          <span>40-50m</span>
        </div>
        <div className="legend-item">
          <span className="legend-color legend-color-50m"></span>
          <span>50-60m</span>
        </div>
        <div className="legend-item">
          <span className="legend-color legend-color-60m"></span>
          <span>60-70m</span>
        </div>
        <div className="legend-item">
          <span className="legend-color legend-color-70m"></span>
          <span>70-80m</span>
        </div>
      </div>

      {/* Tile Downloader Panel */}
      {showDownloader && (
        <TileDownloader mapCenter={mapCenter} mapZoom={mapZoom} />
      )}

      {/* Cache Inspector */}
      {showCacheInspector && <CacheInspector />}

      {/* Scale Ruler - Full Width at Bottom */}
      <div className="scale-bar" data-testid="scale-bar">
        <div className="scale-ruler">
          <div className="scale-markers">
            {/* Left side: -3, -2, -1 */}
            <div className="scale-marker" style={{ left: `${50 - scaleSegmentPercent * 3}%` }}>
              <div className="scale-label">-{scaleDistance * 3} {scaleUnit}</div>
            </div>
            <div className="scale-marker" style={{ left: `${50 - scaleSegmentPercent * 2}%` }}>
              <div className="scale-label">-{scaleDistance * 2} {scaleUnit}</div>
            </div>
            <div className="scale-marker" style={{ left: `${50 - scaleSegmentPercent}%` }}>
              <div className="scale-label">-{scaleDistance} {scaleUnit}</div>
            </div>
            {/* Center: 0 */}
            <div className="scale-marker center-marker" style={{ left: '50%' }}>
              <div className="scale-label">0</div>
            </div>
            {/* Right side: +1, +2, +3 */}
            <div className="scale-marker" style={{ left: `${50 + scaleSegmentPercent}%` }}>
              <div className="scale-label">{scaleDistance} {scaleUnit}</div>
            </div>
            <div className="scale-marker" style={{ left: `${50 + scaleSegmentPercent * 2}%` }}>
              <div className="scale-label">{scaleDistance * 2} {scaleUnit}</div>
            </div>
            <div className="scale-marker" style={{ left: `${50 + scaleSegmentPercent * 3}%` }}>
              <div className="scale-label">{scaleDistance * 3} {scaleUnit}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Loading Indicator */}
      {(loadingBathymetry || loadingPlaceNames) && (
        <div className="loading-indicator">
          <div className="loading-spinner" role="progressbar"></div>
          <div className="loading-content">
            <div className="loading-label">
              {loadingBathymetry && loadingPlaceNames
                ? 'Loading map data...'
                : loadingBathymetry
                ? 'Loading bathymetry contours...'
                : 'Loading place names...'}
            </div>
            <div className="loading-progress">
              <div 
                className="loading-progress-bar" 
                style={{ 
                  width: loadingBathymetry && loadingPlaceNames 
                    ? '33%' 
                    : loadingBathymetry || loadingPlaceNames 
                    ? '66%' 
                    : '100%' 
                }}
              ></div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MapView
